// ============================================
// FAQ 详情页（只读查看，/faq/:id）
// 和编辑器布局一致：左侧 Q/A/N 大纲 + 右侧内容
// ============================================

import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, List, Loader2, Edit3, FileText, Download, Paperclip, Lock, MessageCircleQuestion } from 'lucide-react';
import FaqRichEditor from './FaqRichEditor';
import FaqHtmlSandboxPreview from './FaqHtmlSandboxPreview';
import FaqAttachmentPreview from './FaqAttachmentPreview';
import FaqQuestionModal from './FaqQuestionModal';
import { uploadFaqImage } from './faq-service';
import { useFaqStore } from './faq-store';
import { useUserStore } from '../stores/user-store';
import FaqEditorModal from './FaqEditorModal';
import { useEditingLock } from './use-editing-lock';
import { faqTagLabels } from './faq-tag-utils';
import { useFaqSpace } from './FaqSpaceContext';
import type { FaqItem, FaqAttachment } from './types';

interface HeadingItem {
  id: string;
  text: string;
  level: number;
}

export default function FaqDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { space, path } = useFaqSpace();
  const { items, loading, fetchItems, fetchTags, getPrimaryTags, getSecondaryTags, incrementViewCount } = useFaqStore();
  const { profile } = useUserStore();
  const isAdmin = !!profile?.is_faq_admin;

  const [item, setItem] = useState<FaqItem | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [lockWarning, setLockWarning] = useState<string | null>(null);
  const [answerHeadings, setAnswerHeadings] = useState<HeadingItem[]>([]);
  const [activeHeadingId, setActiveHeadingId] = useState('');
  const contentRef = useRef<HTMLDivElement>(null);
  const [previewAttachment, setPreviewAttachment] = useState<FaqAttachment | null>(null);
  const [askOpen, setAskOpen] = useState(false);

  const { checkLock, acquireLock, releaseLock, lockExpiring, refreshActivity } = useEditingLock();
  const allTags = [...getPrimaryTags(), ...getSecondaryTags()];
  const uploadImage = (file: File) => uploadFaqImage(file, space.slug);

  function handleEditClick() {
    if (!item) return;
    const lockInfo = checkLock(item.id);
    if (lockInfo.locked) {
      setLockWarning(`${lockInfo.lockedBy} 正在编辑此文章，请稍后再试`);
      return;
    }
    if (!acquireLock(item.id)) {
      setLockWarning('无法获取编辑锁，请稍后再试');
      return;
    }
    setEditorOpen(true);
  }
  function handleEditorClose() {
    releaseLock();
    setEditorOpen(false);
  }

  useEffect(() => {
    if (items.length === 0) fetchItems();
    fetchTags();
  }, [items.length, fetchItems, fetchTags]);

  useEffect(() => {
    if (id && items.length > 0) {
      const found = items.find((i) => i.id === id);
      setItem(found || null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, items]);

  useEffect(() => {
    setAnswerHeadings([]);
    setActiveHeadingId('');
  }, [item?.id, item?.answer_format]);

  // 浏览量：同一会话同一文章只计一次
  useEffect(() => {
    if (!id) return;
    const key = `faq_viewed_${space.id}_${id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    incrementViewCount(id);
  }, [id, incrementViewCount, space.id]);

  // 提取回答中的标题
  const handleAnswerEditorReady = (editor: any) => {
    const extractHeadings = () => {
      const container = contentRef.current?.querySelector('#answer-section .ProseMirror');
      if (!container) return;
      const elements = container.querySelectorAll('h1, h2, h3');
      const list: HeadingItem[] = [];
      elements.forEach((el, index) => {
        const headingId = `detail-heading-${index}`;
        el.setAttribute('id', headingId);
        const text = el.textContent || '';
        if (text.trim()) list.push({ id: headingId, text, level: parseInt(el.tagName[1]) });
      });
      setAnswerHeadings(list);
    };
    editor.on('create', extractHeadings);
    setTimeout(extractHeadings, 100);
  };

  const scrollToSection = (sectionId: string) => {
    contentRef.current?.querySelector(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const scrollToHeading = (heading: HeadingItem) => {
    const el = contentRef.current?.querySelector(`#${heading.id}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveHeadingId(heading.id);
    }
  };

  if (loading || !item) {
    return (
      <div className="flex items-center justify-center h-full">
        {loading ? <Loader2 size={24} className="animate-spin text-sky-500" /> : (
          <div className="text-center">
            <p className="text-slate-400 mb-3">未找到该 FAQ</p>
            <button onClick={() => navigate(path('knowledge'))} className="text-sky-500 text-sm hover:underline">返回列表</button>
          </div>
        )}
      </div>
    );
  }

  const isHtmlAnswer = item.answer_format === 'html';

  return (
    <div className="flex h-full overflow-hidden">

      {/* ── 左侧 Q/A/N 大纲 ── */}
      <div className="hidden xl:block w-56 shrink-0 border-r border-slate-100 overflow-y-auto py-6 px-3">
        {/* 返回按钮 */}
        <button
          onClick={() => navigate(path('knowledge'))}
          className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-sky-500 transition mb-5 pl-3"
        >
          <ArrowLeft size={12} /> 返回列表
        </button>

        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-4 pl-3 flex items-center gap-1.5">
          <List size={11} />
          文档结构
        </p>

        <div className="flex flex-col gap-0.5 text-xs">
          {/* Q */}
          <button
            onClick={() => scrollToSection('#detail-section-q')}
            className="w-full text-left px-3 py-2 rounded-lg font-semibold text-sky-600 bg-sky-50/60 flex items-center gap-2"
          >
            <span className="w-5 h-5 rounded-md bg-sky-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">Q</span>
            <span className="truncate">{item.question}</span>
          </button>

          {/* A */}
          <button
            onClick={() => scrollToSection('#detail-section-a')}
            className="w-full text-left px-3 py-2 rounded-lg font-semibold text-emerald-600 bg-emerald-50/60 flex items-center gap-2 mt-1"
          >
            <span className="w-5 h-5 rounded-md bg-emerald-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">A</span>
            回答
          </button>

          {/* A 子标题 */}
          {answerHeadings.length > 0 && (
            <div className="relative border-l border-emerald-200/60 ml-5 flex flex-col gap-0.5">
              {answerHeadings.map((heading) => (
                <button
                  key={heading.id}
                  onClick={() => scrollToHeading(heading)}
                  style={{ paddingLeft: `${(heading.level - 1) * 10 + 10}px` }}
                  className={`w-full text-left py-1.5 pr-2 rounded-r-lg transition-all duration-200 border-l-2 -ml-[1.5px] ${
                    activeHeadingId === heading.id
                      ? 'border-emerald-500 text-emerald-600 font-semibold bg-emerald-50/50'
                      : 'border-transparent text-slate-400 hover:text-slate-600 hover:bg-slate-100/50'
                  }`}
                >
                  <span className="line-clamp-1">{heading.text}</span>
                </button>
              ))}
            </div>
          )}

          {/* N */}
          {item.notes && (
            <button
              onClick={() => scrollToSection('#detail-section-n')}
              className="w-full text-left px-3 py-2 rounded-lg font-semibold text-amber-600 bg-amber-50/60 flex items-center gap-2 mt-1"
            >
              <span className="w-5 h-5 rounded-md bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">N</span>
              附件与备注
            </button>
          )}
        </div>
      </div>

      {/* ── 内容区 ── */}
      <div ref={contentRef} className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-4 py-5 sm:px-8 sm:py-8 space-y-4 sm:space-y-6">

          {/* 移动端返回 */}
          <button
            onClick={() => navigate(path('knowledge'))}
            className="xl:hidden flex items-center gap-1.5 text-xs text-slate-400 hover:text-sky-500 transition mb-2"
          >
            <ArrowLeft size={12} /> 返回列表
          </button>

          {/* ═══ Q 问题 ═══ */}
          <div id="detail-section-q" className="rounded-2xl border border-sky-200/60 bg-gradient-to-r from-sky-50/50 to-transparent overflow-hidden">
            <div className="flex items-center justify-between px-6 py-3 border-b border-sky-100 bg-sky-50/80">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-lg bg-sky-500 text-white text-xs font-bold flex items-center justify-center">Q</span>
                <span className="text-sm font-semibold text-sky-700">问题</span>
              </div>
              <span className="flex items-center gap-1 text-xs text-slate-400"><Eye size={12} /> {item.view_count}</span>
            </div>
            <div className="px-6 py-5 space-y-4">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-700">{item.question}</h1>
              <div className="flex items-center gap-2 flex-wrap">
                {faqTagLabels(item, allTags, 1).map((t) => (
                  <span key={t} className="px-2 py-0.5 bg-sky-50 text-sky-600 text-[11px] font-medium rounded-md">{t}</span>
                ))}
                {faqTagLabels(item, allTags, 2).map((t) => (
                  <span key={t} className="px-2 py-0.5 bg-purple-50 text-purple-500 text-[11px] font-medium rounded-md">{t}</span>
                ))}
              </div>
              {item.cover_image && (
                <div className="bg-slate-50 rounded-lg overflow-hidden">
                  <img src={item.cover_image} alt="封面" className="w-full max-h-[400px] object-contain" />
                </div>
              )}
            </div>
          </div>

          {/* ═══ A 回答 ═══ */}
          {item.answer && (
            <div id="detail-section-a" className="rounded-2xl border border-emerald-200/60 bg-gradient-to-r from-emerald-50/50 to-transparent overflow-hidden">
              <div className="flex items-center gap-2 px-6 py-3 border-b border-emerald-100 bg-emerald-50/80">
                <span className="w-6 h-6 rounded-lg bg-emerald-500 text-white text-xs font-bold flex items-center justify-center">A</span>
                <span className="text-sm font-semibold text-emerald-700">回答</span>
              </div>
              <div id="answer-section" className="px-4 sm:px-6 py-4 sm:py-5 bg-white">
                {isHtmlAnswer ? (
                  <FaqHtmlSandboxPreview html={item.answer} minHeight="720px" />
                ) : (
                  <FaqRichEditor
                    content={item.answer}
                    readOnly
                    uploadImage={uploadImage}
                    minHeight="100px"
                    onEditorReady={handleAnswerEditorReady}
                  />
                )}
              </div>
            </div>
          )}

          {/* ═══ N 附件与备注 ═══ */}
          {(item.notes || (item.attachments && item.attachments.length > 0)) && (
            <div id="detail-section-n" className="rounded-2xl border border-amber-200/60 bg-gradient-to-r from-amber-50/50 to-transparent overflow-hidden">
              <div className="flex items-center gap-2 px-6 py-3 border-b border-amber-100 bg-amber-50/80">
                <span className="w-6 h-6 rounded-lg bg-amber-500 text-white text-xs font-bold flex items-center justify-center">N</span>
                <span className="text-sm font-semibold text-amber-700">附件与备注</span>
              </div>
              <div className="px-4 sm:px-6 py-4 sm:py-5 bg-white space-y-4">
                {item.notes && (
                  <FaqRichEditor content={item.notes} readOnly uploadImage={uploadImage} minHeight="60px" />
                )}
                {item.attachments && item.attachments.length > 0 && (
                  <div className="space-y-1.5">
                    <p className="text-xs font-semibold text-amber-500/80 uppercase tracking-wider flex items-center gap-1.5">
                      <Paperclip size={12} />
                      附件 ({item.attachments.length})
                    </p>
                    <div className="space-y-1">
                      {item.attachments.map((att, idx) => (
                        <div
                          key={idx}
                          className="flex items-center gap-3 px-3 py-2 bg-amber-50/50 border border-amber-100 rounded-xl hover:bg-amber-50 hover:border-amber-200 transition-all group"
                        >
                          <FileText size={16} className="text-amber-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-600 truncate">{att.name}</p>
                            <p className="text-[10px] text-slate-400">{(att.size / 1024).toFixed(1)} KB · {att.type.split('/').pop()}</p>
                          </div>
                          <button
                            onClick={() => setPreviewAttachment(att)}
                            className="px-2.5 py-1 text-xs font-medium text-amber-600 bg-amber-100/60 hover:bg-amber-100 rounded-lg transition-colors"
                          >
                            预览
                          </button>
                          <a
                            href={att.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-1.5 text-slate-400 hover:text-sky-500 rounded-lg transition-colors"
                            title="下载"
                          >
                            <Download size={14} />
                          </a>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={() => setAskOpen(true)}
              className="flex items-center gap-1.5 px-5 py-2.5 bg-sky-50 text-sky-600 text-sm font-semibold rounded-xl hover:bg-sky-100 transition-all"
            >
              <MessageCircleQuestion size={14} />
              针对此 FAQ 提问
            </button>

          {/* 管理员编辑按钮 */}
          {isAdmin && (
              <button
                onClick={handleEditClick}
                className="flex items-center gap-1.5 px-5 py-2.5 bg-sky-500 text-white text-sm font-semibold rounded-xl hover:bg-sky-600 transition-all shadow-sm"
              >
                <Edit3 size={14} /> 编辑此 FAQ
              </button>
          )}
          </div>
        </div>

        {/* 悬浮返回按钮 — 滚动后始终可见 */}
        <button
          onClick={() => navigate(path('knowledge'))}
          className="fixed bottom-6 left-4 z-30 xl:hidden flex items-center gap-1.5 px-4 py-2.5 bg-white/90 backdrop-blur-sm text-slate-600 text-sm font-medium rounded-full shadow-lg border border-slate-200 hover:bg-slate-50 transition-all active:scale-95"
        >
          <ArrowLeft size={16} />
          返回
        </button>
      </div>

      {/* 编辑弹窗 */}
      {isAdmin && (
        <FaqEditorModal
          isOpen={editorOpen}
          onClose={handleEditorClose}
          editingItem={item}
          lockExpiring={lockExpiring}
          onRefreshActivity={refreshActivity}
        />
      )}

      <FaqQuestionModal isOpen={askOpen} onClose={() => setAskOpen(false)} relatedFaq={item} />

      {/* 编辑锁冲突提示 */}
      {lockWarning && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setLockWarning(null)} />
          <div className="relative bg-white rounded-2xl shadow-2xl p-6 max-w-sm mx-4 text-center">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <Lock size={22} className="text-amber-500" />
            </div>
            <h3 className="text-base font-bold text-slate-800 mb-1">文章编辑中</h3>
            <p className="text-sm text-slate-500 mb-4">{lockWarning}</p>
            <button
              onClick={() => setLockWarning(null)}
              className="px-6 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-xl hover:bg-slate-200 transition-all"
            >知道了</button>
          </div>
        </div>
      )}

      {/* 附件预览弹窗 */}
      <FaqAttachmentPreview
        attachment={previewAttachment}
        isOpen={!!previewAttachment}
        onClose={() => setPreviewAttachment(null)}
      />
    </div>
  );
}
